import { runPdfExportSafely, showPrintDialogSafely, toastExportError, toastExportInfo } from "./printExportFix";
import { JWT_TOKEN, PDF_EXPORT_SERVER_HOST } from "../../../global/constants";
import { printElement } from "../../../utils/print/printRoot";
import "./exportMask.scss";
import { trackEvent } from "../../../utils/analytics/trackEvent";

export const PDF_EXPORT_ROW_CAP = 5000;
export const PRINT_ROW_CAP = 300;
export const EXPORT_TIMEOUT_MS = 120000;
export const HEALTH_TIMEOUT_MS = 5000;
const HEALTH_CACHE_MS = 5 * 60 * 1000;
const HEALTH_CACHE_KEY = "pdfExportServerUp";

// Bryntum replaces its whole headers object with fetchOptions.headers, so the
// JSON content type must be repeated here. Read the token at call time: it
// changes on re-login and tenant switch.
const readToken = () => {
  try {
    return localStorage.getItem(JWT_TOKEN) || "";
  } catch (e) {
    return "";
  }
};

export const exportFetchOptions = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${readToken()}`,
  },
});

export const clearExportServerCache = () => {
  try {
    sessionStorage.removeItem(HEALTH_CACHE_KEY);
  } catch (e) {
    // storage unavailable: nothing is cached
  }
};

const probeState = (response) => {
  if (response?.ok) return "up";
  if (response?.status === 401 || response?.status === 403) return "unauthorized";
  return "down";
};

// "up" | "down" | "unauthorized". Only up/down are cached: an expired session is
// answered again once the user signs back in.
export const checkExportServer = async ({ fetchImpl, now = Date.now } = {}) => {
  let timer;
  try {
    try {
      const cached = JSON.parse(sessionStorage.getItem(HEALTH_CACHE_KEY));
      if ((cached?.state === "up" || cached?.state === "down") && now() - cached.at < HEALTH_CACHE_MS) {
        return cached.state;
      }
    } catch (e) {
      clearExportServerCache();
    }
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve("down"), HEALTH_TIMEOUT_MS);
    });
    // The default fetch is resolved inside the chain: window.fetch may be undefined (jsdom).
    const probe = Promise.resolve()
      .then(() => (fetchImpl || window.fetch.bind(window))(`${PDF_EXPORT_SERVER_HOST}status`, { method: "GET", ...exportFetchOptions() }))
      .then(probeState)
      .catch(() => "down");
    const state = await Promise.race([probe, timeout]);
    if (state !== "unauthorized") {
      try {
        sessionStorage.setItem(HEALTH_CACHE_KEY, JSON.stringify({ state, at: now() }));
      } catch (e) {
        // storage unavailable: just don't cache
      }
    }
    return state;
  } catch (e) {
    return "down";
  } finally {
    clearTimeout(timer);
  }
};

const pad2 = (n) => String(n).padStart(2, "0");
const localDate = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const sanitise = (value) => String(value ?? "").replace(/[\\/:*?"<>|]+/g, "-").trim();

export const buildExportFileName = ({ name, view, date = new Date() } = {}) => {
  const safeName = sanitise(name).slice(0, 80).trim() || "export";
  const safeView = sanitise(view) || "gantt";
  return `${safeName}_${safeView}_${localDate(date)}.pdf`;
};

export const ganttExportFeatures = (t, { name, view }) => ({
  pdfExportFeature: {
    disabled: false,
    exportServer: PDF_EXPORT_SERVER_HOST,
    translateURLsToAbsolute: true,
    sendAsBinary: true,
    showErrorToast: false,
    exporterType: "multipagevertical",
    orientation: "landscape",
    paperFormat: "A3",
    repeatHeader: true,
    rowsRange: "all",
    scheduleRange: "currentview",
    // Defaults only: runExport refreshes fetchOptions (token) and passes fileName
    // per export, so consumers must not rely on these config-time values.
    fileName: buildExportFileName({ name, view }),
    fetchOptions: exportFetchOptions(),
    exportDialog: {
      items: {
        exporterTypeField: {
          value: "multipagevertical",
          items: [
            { value: "multipagevertical", text: t("gantt.export.layoutFitWidth") },
            { value: "multipage", text: t("gantt.export.layoutPagesAcross") },
          ],
        },
        paperFormatField: { items: ["A3", "A4"], value: "A3" },
        rowsRangeField: { value: "all" },
      },
    },
  },
  printFeature: {
    disabled: false,
    orientation: "landscape",
    paperFormat: "A3",
    exporterType: "singlepage",
  },
});

// Bryntum clones client.element into the exported pages, so nothing of ours may live inside it:
// the button is a fixed overlay on document.body, centred over the client's box, above Bryntum's
// mask (z-index 10000, re-created at every step).
const addCancelButton = (client, t, onCancel) => {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "b-button b-raised pdf-export-cancel";
  button.setAttribute("data-testid", "pdf-export-cancel");
  button.textContent = t("gantt.export.cancel");
  Object.assign(button.style, { position: "fixed", transform: "translateX(-50%)", zIndex: "10001" });
  const place = () => {
    const box = client.element.getBoundingClientRect();
    button.style.left = `${box.left + box.width / 2}px`;
    button.style.top = `${box.top + box.height / 2 + 40}px`;
  };
  place();
  button.addEventListener("click", onCancel);
  document.body.appendChild(button);
  window.addEventListener("resize", place);
  window.addEventListener("scroll", place, true);
  const observer = typeof ResizeObserver === "function" ? new ResizeObserver(place) : null;
  observer?.observe(client.element);
  return {
    button,
    dispose: () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      observer?.disconnect();
      button.remove();
    },
  };
};

// After a timeout the export keeps running until Bryntum reaches its fetch (which the
// aborted signal then refuses); wait for it, but never longer than this.
export const EXPORT_SETTLE_GRACE_MS = 30000;

const settleWithin = (promise, ms) => {
  if (!promise) return undefined;
  let timer;
  return Promise.race([
    Promise.resolve(promise).catch(() => {}),
    new Promise((resolve) => {
      timer = setTimeout(resolve, ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

// Bryntum 5.6's AjaxHelper.fetch replaces options.signal with its own AbortController, so the
// signal in fetchOptions never reaches fetch: bridge it to the request promise's abort().
const bridgeAbortSignal = (pdfExport) => {
  if (pdfExport.$offolioAbortBridged) return;
  pdfExport.$offolioAbortBridged = true;
  const receive = pdfExport.receiveExportContent;
  pdfExport.receiveExportContent = function receiveWithSignal(...args) {
    const request = receive.apply(this, args);
    const signal = this.fetchOptions?.signal;
    if (signal && request?.abort) {
      if (signal.aborted) request.abort();
      else signal.addEventListener("abort", () => request.abort(), { once: true });
    }
    return request;
  };
};

// One gantt.export_pdf event per attempt, at settle time. Never carries the token, markup or user data.
// server: "up" | "down" | "unauthorized" (the health probe's answer), or "client" when Syncfusion's
// in-browser pivot exporter rendered the PDF and no server was involved.
export const emitExportEvent = ({ startedAt, rows, pages, view, result, server }) => {
  const durationMs = Math.max(0, Date.now() - startedAt);
  trackEvent("gantt.export_pdf", {
    ...(rows !== undefined ? { rows } : {}),
    ...(pages !== undefined ? { pages } : {}),
    durationMs,
    view,
    result,
    server,
  });
};

// The error mapping shared by every export that reaches the server. Returns true when the error
// got its own toast; false leaves the generic failure toast to the caller.
export const handleExportError = (e, t, { onTimeout } = {}) => {
  if (e?.status === 401 || e?.status === 403) {
    toastExportError(t("gantt.export.unauthorized"));
    return true;
  }
  if (e?.status === 429) {
    toastExportError(t("gantt.export.rateLimited"));
    return true;
  }
  // A hung server, a 5xx or a network error (no status): probe again on the next click.
  if (e?.name === "TimeoutError") {
    clearExportServerCache();
    onTimeout?.();
    toastExportError(t("gantt.export.timeout", { seconds: EXPORT_TIMEOUT_MS / 1000 }));
    return true;
  }
  if (!e?.status || e.status >= 500) clearExportServerCache();
  return false;
};

// "Visible schedule" exports the window on screen, but Bryntum 5.6 derives it by scrolling the
// timeline, and on a wide buffered time axis (after zooming: e.g. 2010-2046 at year-30by100) it
// waits for a scroll that never comes: no request, no error, and the Gantt stays "exporting"
// until reload. The same window passed as explicit dates exports normally.
//
// The visible range can also come out empty (startDate === endDate): seen with the browser zoomed
// below 100 % (devicePixelRatio 1.67, 1056 px wide), where the export then stalls the same way.
// An empty window falls back to the project's span, then to the time axis.
//
// visibleDateRange is also stale until the timeline is scrolled once: right after load on a
// zoomed-in planning it reported 6 days for a window showing months. The window is read from the
// timeline's scroll position first.
const spanOf = (start, end) => (start && end && +new Date(end) > +new Date(start) ? { start, end } : null);

const scrolledSpan = (client) => {
  const scrollable = client.timeAxisSubGrid?.scrollable;
  const model = client.timeAxisViewModel;
  const width = client.timeAxisSubGrid?.width;
  if (!scrollable || !model?.getDateFromPosition || !width) return null;
  return spanOf(model.getDateFromPosition(scrollable.x), model.getDateFromPosition(scrollable.x + width));
};

export const pinVisibleRange = (client, values) => {
  if (values?.scheduleRange !== "currentview") return values;
  const visible = client.visibleDateRange;
  const span =
    scrolledSpan(client) ||
    spanOf(visible?.startDate, visible?.endDate) ||
    spanOf(client.project?.startDate, client.project?.endDate) ||
    spanOf(client.timeAxis?.startDate, client.timeAxis?.endDate);
  if (!span) return values;
  return { ...values, scheduleRange: "daterange", rangeStart: span.start, rangeEnd: span.end };
};

export const runExport = async (client, options) => {
  const startedAt = Date.now();
  const progress = {};
  let result = "failed";
  try {
    result = await runExportInner(client, options, progress);
    return result;
  } finally {
    if (result !== "busy") emitExportEvent({
      startedAt, rows: options.rowsCount ?? undefined, pages: progress.pages, view: options.view, result,
      server: options.serverUp === false ? "down" : "up",
    });
  }
};

const runExportInner = async (client, { values = {}, t, rowsCount = 0, fileName, serverUp, onSettled }, progress) => {
  // Also true while a timed-out export is still winding down, and during a browser print.
  if (client.isExporting) return "busy";
  if (rowsCount > PDF_EXPORT_ROW_CAP) {
    toastExportError(t("gantt.export.tooManyRows", { rows: rowsCount, cap: PDF_EXPORT_ROW_CAP }));
    return "refused";
  }
  if (serverUp === false) {
    // Schedulers and the workload grid have no print feature to fall back to.
    if (!client.features?.print) {
      toastExportError(t("gantt.export.serverUnavailable"));
      return "failed";
    }
    if (rowsCount > PRINT_ROW_CAP) {
      toastExportError(t("gantt.export.serverDownTooLarge", { limit: PRINT_ROW_CAP }));
      return "failed";
    }
    toastExportInfo(t("gantt.export.serverDownFallback"));
    await showPrintDialogSafely(client);
    return "printed";
  }

  const pdfExport = client.features.pdfExport;
  const controller = new AbortController();
  let outcome = "failed";
  let failed = false;
  let stopping = false;
  let exporting;
  let cancel;
  const maskText = (percent) =>
    stopping ? t("gantt.export.cancelling") : t("gantt.export.progress", { percent });
  const onStep = ({ progress: percent, pages }) => {
    if (Number.isFinite(pages)) progress.pages = pages;
    client.mask({ text: maskText(Math.round(percent || 0)) });
  };
  // The abort only bites when Bryntum sends the request; until then the pages keep
  // being generated, so the mask says so instead of the progress.
  const stop = () => {
    if (stopping) return;
    stopping = true;
    if (cancel) cancel.button.disabled = true;
    controller.abort();
    client.mask({ text: maskText() });
  };
  const cleanup = () => {
    // Unmask first: nothing below may keep the Gantt masked if it throws.
    try { client.unmask(); } catch (e) { /* nothing more to restore */ }
    try { onSettled?.(); } catch (e) { /* caller's restore must not break the rest of the cleanup */ }
    try { cancel?.dispose(); } catch (e) { /* button already gone */ }
    try { pdfExport.un("exportStep", onStep); } catch (e) { /* listener already gone */ }
  };

  try {
    client.mask({ text: maskText(0) });
    pdfExport.on("exportStep", onStep);
    bridgeAbortSignal(pdfExport);
    cancel = addCancelButton(client, t, stop);

    await runPdfExportSafely(
      async () => {
        // Cancel can land before this microtask runs; an abort event fired earlier is never replayed.
        if (controller.signal.aborted) throw Object.assign(new Error("aborted"), { name: "AbortError" });
        // Bryntum reads fetchOptions (and the bridge its signal) when it sends the request, so
        // concurrent exports on one Gantt would share it: runExport refuses while client.isExporting, and the callers keep
        // their export button disabled for the whole run. The aborted signal stays in place
        // after a timeout until the next export installs its own.
        pdfExport.fetchOptions = { ...exportFetchOptions(), signal: controller.signal };
        exporting = pdfExport.export({ ...pinVisibleRange(client, values), ...(fileName ? { fileName } : {}) });
        const result = await exporting;
        if (!failed) outcome = "exported";
        const known = pdfExport.exporter?.pages?.length;
        if (progress.pages === undefined && known) progress.pages = known;
        return result;
      },
      {
        timeoutMs: EXPORT_TIMEOUT_MS,
        onError: (e) => {
          // A non-ok response resolves instead of rejecting, so outcome may already read "exported".
          failed = true;
          outcome = "failed";
          if (controller.signal.aborted || e?.name === "AbortError") {
            outcome = "cancelled";
            toastExportInfo(t("gantt.export.cancelled"));
            return true;
          }
          return handleExportError(e, t, { onTimeout: stop });
        },
      }
    );
    // Keeps the mask, the button and isExporting until the real export has stopped.
    await settleWithin(exporting, EXPORT_SETTLE_GRACE_MS);
  } finally {
    cleanup();
  }
  return outcome;
};

const nextFrame = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Scheduler PDF button: fit the whole schedule, export through runExport, then restore the zoom.
export const exportSchedulerPdfSafely = async (sch, { t, name = "scheduler" }) => {
  const zoomLevel = sch.zoomLevel;
  const startDate = sch.startDate;
  const endDate = sch.endDate;
  const viewPreset = sch.viewPreset;
  const restoreView = () => {
    sch.viewPreset = viewPreset;
    sch.zoomTo({ level: zoomLevel, startDate, endDate });
  };
  if (sch.$offolioExporting || sch.isExporting) return "busy";
  sch.$offolioExporting = true;
  const options = {
    values: { orientation: "landscape", paperFormat: "A3", scheduleRange: "completeview" },
    t,
    rowsCount: sch.store?.count ?? 0,
    fileName: buildExportFileName({ name, view: "scheduler" }),
    onSettled: restoreView,
  };
  let viewChanged = false;
  try {
    const state = await checkExportServer();
    if (state === "unauthorized") {
      toastExportError(t("gantt.export.unauthorized"));
      return "failed";
    }
    const serverUp = state === "up";
    // Refused (row cap) returns before runExport's cleanup: leave the view alone.
    if (serverUp && options.rowsCount <= PDF_EXPORT_ROW_CAP) {
      viewChanged = true;
      sch.zoomToFit({ leftMargin: 20, rightMargin: 20 });
      await nextFrame(500);
      sch.viewPreset = "monthAndYear";
      await nextFrame(800);
    }
    return await runExport(sch, { ...options, serverUp });
  } finally {
    if (viewChanged) restoreView();
    sch.unmask?.();
    sch.$offolioExporting = false;
  }
};

// Cross-origin sheets (CDN fonts) throw on cssRules access; the export server loads those itself.
export const collectAppCss = ({ fontsOnly = false } = {}) =>
  [...document.styleSheets]
    .map((sheet) => {
      try {
        return [...sheet.cssRules]
          .filter((r) => !fontsOnly || r.type === CSSRule.FONT_FACE_RULE)
          .map((r) => r.cssText)
          .join("\n");
      } catch (e) {
        return "";
      }
    })
    .join("\n");

export const escapeHtml = (value) =>
  String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// css "fonts" keeps only @font-face rules: self-contained SVG exports carry their own styles inline.
export const buildPrintDocument = ({ html, title, orientation = "landscape", paper = "A3", css = "all" }) =>
  `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${collectAppCss({ fontsOnly: css === "fonts" })}\n@page { size: ${paper} ${orientation}; margin: 10mm }\nbody { margin: 0; background: #fff }</style></head><body>${html}</body></html>`;

// Always a fixed overlay on document.body: a mask inside the exported element would end up in the PDF.
export const showExportMask = (t, onCancel) => {
  const mask = document.createElement("div");
  mask.className = "offolio-export-mask";
  mask.setAttribute("data-testid", "export-mask");
  const text = document.createElement("div");
  text.className = "offolio-export-mask-text";
  // A single POST reports no progress, unlike Bryntum's page-by-page export.
  text.textContent = t("gantt.export.exporting");
  mask.appendChild(text);
  // No handler means the export cannot be cancelled: show no button.
  if (onCancel) {
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.setAttribute("data-testid", "pdf-export-cancel");
    cancel.textContent = t("gantt.export.cancel");
    cancel.addEventListener("click", onCancel);
    mask.appendChild(cancel);
  }
  document.body.appendChild(mask);
  return () => mask.remove();
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const exportsInFlight = new Set();

export const exportHtmlToPdf = async (options) => {
  const { html, view, name, t } = options;
  if (!html || !String(html).trim()) {
    toastExportError(t("taskFocus.exportToPdfFailed"));
    emitExportEvent({ startedAt: Date.now(), view, result: "refused", server: "up" });
    return "refused";
  }
  const key = `${view}:${name}`;
  if (exportsInFlight.has(key)) return "busy";
  exportsInFlight.add(key);
  const startedAt = Date.now();
  const meta = { server: "up" };
  let result = "failed";
  try {
    result = await runHtmlExport(options, meta);
    return result;
  } finally {
    emitExportEvent({ startedAt, pages: result === "exported" ? 1 : undefined, view, result, server: meta.server });
    exportsInFlight.delete(key);
  }
};

const runHtmlExport = async ({
  html,
  title,
  name,
  view,
  t,
  orientation = "landscape",
  paper = "A3",
  css = "all",
  element,
  onClone,
  onServerRender,
  fetchImpl = (...args) => window.fetch(...args),
}, meta = {}) => {
  const fileName = buildExportFileName({ name, view });
  const state = await checkExportServer({ fetchImpl });
  meta.server = state;
  if (state === "unauthorized") {
    toastExportError(t("gantt.export.unauthorized"));
    return "failed";
  }
  if (state !== "up") {
    toastExportInfo(t("gantt.export.serverDownFallback"));
    const temp = element || Object.assign(document.createElement("div"), { innerHTML: html });
    try {
      await printElement(temp, { title, orientation, paper, onClone });
      return "printed";
    } catch (e) {
      toastExportError(t("gantt.export.printFailed"));
      return "failed";
    }
  }

  // The markup is final from here on: a caller that expanded its view for the fallback can restore it.
  onServerRender?.();
  const controller = new AbortController();
  const removeMask = showExportMask(t, () => controller.abort());
  let timer;
  let timedOut = false;
  try {
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(Object.assign(new Error("pdf export timeout"), { name: "TimeoutError" }));
      }, EXPORT_TIMEOUT_MS);
    });
    const request = (async () => {
      const response = await fetchImpl(PDF_EXPORT_SERVER_HOST, {
        method: "POST",
        ...exportFetchOptions(),
        signal: controller.signal,
        body: JSON.stringify({
          html: [{ html: buildPrintDocument({ html, title, orientation, paper, css }) }],
          orientation,
          format: paper,
          fileFormat: "pdf",
          fileName,
          sendAsBinary: true,
          clientURL: window.location.origin,
        }),
      });
      if (!response.ok) return { response };
      return { response, blob: await response.blob() };
    })();
    request.catch(() => {});
    const { response, blob } = await Promise.race([request, timeout]);
    if (!response.ok) {
      throw Object.assign(new Error(`pdf export server answered ${response.status}`), { status: response.status });
    }
    downloadBlob(blob, fileName);
    return "exported";
  } catch (e) {
    if (controller.signal.aborted && !timedOut) {
      toastExportInfo(t("gantt.export.cancelled"));
      return "cancelled";
    }
    // The aborted fetch can reject before the timeout does: report the timeout either way.
    if (!handleExportError(timedOut ? { name: "TimeoutError" } : e, t)) {
      toastExportError(t("taskFocus.exportToPdfFailed"));
    }
    return "failed";
  } finally {
    clearTimeout(timer);
    removeMask();
  }
};
